import React, { useState, useMemo, useCallback } from 'react';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import {
  Button,
  Typography,
  Paper,
  Box,
  useTheme,
  useMediaQuery,
  Theme,
  TextField,
  MenuItem,
} from '@mui/material';
import { AppRow } from '../../layout/appRow';
import WestIcon from '@mui/icons-material/West';
import {
  MonthSelect,
  ProjectsSummary,
  ReportFilters,
} from '../../models/reports';
import { useGetReportsActivitiesQuery } from '../../store/reports/reportsActivitiesService';
import { ROLES_ID } from '../../models/employee';
import { ViewMode } from '../../models/preferences';
import { format } from 'date-fns';
import {
  formatDisplayReportsDate,
  getDateByWeekNumber,
  getEndDateAccording,
  getStartDateAccording,
} from '../../services/dateService';
import { AppActivityViews } from '../app/appActivityViews';
import { useHandleDisplayDate } from '../../hooks/useHandleDisplayDate';
import { getNextDate, getPreviousDate } from '../../services/reportsService';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { useDisplayDate } from '../../hooks/useDisplayDate';
import { MyCoolGradient } from '../app/button/myCoolGradient';
import { SmallLayoutRouter } from './reportsPageSmallLayout';
import { AppActivityTableViews } from '../app/appActivityTableViews';
import { viewModeOptions } from '../../constants';
import {
   formatDateOnly,
} from '../../services/dateService';

const makeStyles = (theme: Theme) => ({
  mainContentBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  filters: {
    mb: 2,
  },
  gradientBtn: {
    textDecorationColor: ColorPalette.GREEN,
    fontFamily: TypographyFamily.NEXA,
    fontSize: 16,
  },
  projectBox: {
    p: '20px 15px 20px 15px',
    width: '100%',
  },
  worktimeBox: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  name: {
    color: ColorPalette.RAISIN_BLACK,
    fontWeight: '800',
    fontFamily: TypographyFamily.NEXA,
    fontSize: '24px !important',
    lineHeight: '28.8px',
  },
  text: {
    fontWeight: '400',
    fontSize: '14px !important',
    fontFamily: TypographyFamily.ARIAL,
  },
  currentPeriodRow: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: '20px 0px 20px',
  },
  currentPeriodBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
  },
  buttonArrow: {
    color: ColorPalette.DARK_GRAY,
    minWidth: '40px',
  },
  buttonDate: {
    pt: 1.3,
    pr: 0,
    pl: 0,
    ml: 1,
  },

  workedTime: {
    fontSize: '14px !important',
    display: 'inline-flex',
    alignItems: 'center',
    color: ColorPalette.DARK_GRAY,
    mr: 2,
  },
  customPeriodBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: 2,
    marginTop: 3,
  },
  viewModeMobile: { minWidth: '200px', marginTop: '20px' },
  backBtnContent: { display: 'flex', alignItems: 'center', fontSize: 16 },
  backIcon: {
    mr: 2,
    fill: `url(#my-cool-gradient) ${theme.palette.primary.main}`,
  },
});

interface ReportsProjectDetailsProps {
  filters: ReportFilters;
  selectedProject: ProjectsSummary;
  setSelectedProject: (projectSummary: ProjectsSummary | null) => void;
  setRoute: (arg: SmallLayoutRouter) => void;
}

export function ReportsProjectDetails({
  filters,
  selectedProject,
  setRoute,
  setSelectedProject,
}: ReportsProjectDetailsProps): JSX.Element {
  const theme = useTheme();
  const styles = makeStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    startDate: startDateFilter,
    endDate: endDateFilter,
    employeeId,
  } = filters;

  const { loggedMDs, project } = selectedProject;

  const [dateActivity, setDateActivity] = useState<Date>(
    new Date(formatDateOnly(startDateFilter))
  );
  const [viewMode, setViewMode] = useState(ViewMode.MONTH);

  const isCustomDate = useMemo(() => {
    return filters && filters.monthSelect === MonthSelect.CUSTOM;
  }, [filters]);

  const startDate = useMemo(
    () =>
      isCustomDate && viewMode === ViewMode.OLD_VIEW
        ? format(new Date(formatDateOnly(startDateFilter)), 'yyyy-MM-dd')
        : format(getStartDateAccording(viewMode, dateActivity), 'yyyy-MM-dd'),
    [dateActivity, viewMode, isCustomDate, startDateFilter]
  );
  const endDate = useMemo(
    () =>
      isCustomDate && viewMode === ViewMode.OLD_VIEW
        ? format(new Date(formatDateOnly(endDateFilter)), 'yyyy-MM-dd')
        : format(getEndDateAccording(viewMode, dateActivity), 'yyyy-MM-dd'),
    [dateActivity, viewMode, isCustomDate, endDateFilter]
  );

  const startDateFormatted = useMemo(
    () => formatDisplayReportsDate(startDate),
    [startDate]
  );
  const endDateFormatted = useMemo(
    () => formatDisplayReportsDate(endDate),
    [endDate]
  );

  const { displayDate } = useDisplayDate({ viewMode, dateActivity, isMobile });

  const {
    isLoading,
    isFetching,
    data: activities = [],
  } = useGetReportsActivitiesQuery({
    startDate,
    endDate,
    employeeId: employeeId,
    roleId: ROLES_ID.PROJECT_MANAGER,
    projectId: project.id,
  });

  const isLoadingAll = useMemo(
    () => isLoading || isFetching,
    [isFetching, isLoading]
  );

  const onDayActivities = useCallback((activityDate: string) => {
    setDateActivity(new Date(formatDateOnly(activityDate)));
    setViewMode(ViewMode.DAY);
  }, []);

  const onWeekActivities = useCallback(
    (weekNumber: number) => {
      setDateActivity(getDateByWeekNumber(weekNumber, dateActivity));
      setViewMode(ViewMode.WORK_WEEK);
    },
    [dateActivity]
  );

  const handleChangeViewMode = useCallback(
    (viewModeArg: ViewMode) => setViewMode(viewModeArg),
    []
  );

  const handleChangeMobileViewMode = useCallback((event) => {
    setViewMode(event.target.value);
  }, []);

  const { handleDisplayDate } = useHandleDisplayDate({
    viewMode,
    handleChangeViewMode,
  });

  const handlePreviousDate = useCallback(() => {
    setDateActivity(getPreviousDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const handleNextDate = useCallback(() => {
    setDateActivity(getNextDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  return (
    <>
      <AppRow sx={styles.filters}>
        <Button
          onClick={() => {
            setRoute(SmallLayoutRouter.RESULTS);
            setSelectedProject(null);
          }}
        >
          <Typography variant="h2" fontSize={16} sx={styles.backBtnContent}>
            <MyCoolGradient />
            <WestIcon sx={styles.backIcon} />
            <u style={styles.gradientBtn}>Back</u>
          </Typography>
        </Button>
        <Button
          onClick={() => {
            setRoute(SmallLayoutRouter.FILTERS);
            setSelectedProject(null);
          }}
        >
          <Typography variant="h2">
            <u style={styles.gradientBtn}>Filters page</u>
          </Typography>
        </Button>
      </AppRow>
      <Box sx={styles.mainContentBox}>
        <Paper sx={styles.projectBox}>
          <Typography sx={styles.name}>{project.name}</Typography>
          <Typography sx={styles.text}>{project.client}</Typography>
          <Box sx={styles.worktimeBox}>
            <Typography sx={styles.text}>{`Code: ${project.code}`}</Typography>
            <Box>
              <Typography
                sx={styles.workedTime}
              >{`${loggedMDs} MD`}</Typography>
            </Box>
          </Box>
        </Paper>

        {isMobile && (
          <TextField
            select
            fullWidth
            label="View mode"
            sx={styles.viewModeMobile}
            value={viewMode}
            onChange={handleChangeMobileViewMode}
          >
            {viewModeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {isCustomDate ? (
          <Box sx={styles.customPeriodBox}>
            <Typography variant="h1">{startDateFormatted}&nbsp;-</Typography>
            <Typography variant="h1">&nbsp;{endDateFormatted}</Typography>
          </Box>
        ) : (
          <AppRow sx={styles.currentPeriodRow}>
            <Box sx={styles.currentPeriodBox}>
              <Button sx={styles.buttonArrow} onClick={handlePreviousDate}>
                <ArrowBackIosRoundedIcon
                  fontSize={isMobile ? 'small' : 'medium'}
                />
              </Button>
              <Button sx={styles.buttonArrow} onClick={handleNextDate}>
                <ArrowForwardIosRoundedIcon
                  fontSize={isMobile ? 'small' : 'medium'}
                />
              </Button>
              <Button sx={styles.buttonDate} onClick={handleDisplayDate}>
                <Typography
                  variant="h1"
                  sx={{
                    ...(isMobile && { fontSize: '16px !important' }),
                    verticalAlign: 'middle',
                  }}
                >
                  {displayDate}
                </Typography>
              </Button>
            </Box>
          </AppRow>
        )}

        {viewMode === ViewMode.OLD_VIEW ? (
          <AppActivityTableViews
            startDate={startDate}
            endDate={endDate}
            activities={activities}
          />
        ) : (
          <AppActivityViews
            activities={activities}
            dateActivity={dateActivity}
            viewMode={viewMode}
            isLoading={isLoadingAll}
            location={filters.employeeLocation}
            onDayActivities={onDayActivities}
            onWeekActivities={onWeekActivities}
            showOnlyloggedTime
          />
        )}
      </Box>
    </>
  );
}
